import type {PortalGrouping} from "../../dibAppApi/dibAppApi";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {bffApi} from "../chat/bffApi";
import {
    getLangCodeFromEnum,
    getApiLanguageCode,
    LanguageEnum,
} from "../../types";
import type {PortalFull} from "./types";

function isGroupingRelevant(
    grouping: PortalGrouping,
    userDbIds: Set<string>
): boolean {
    const products: string[] = grouping.products_json
        ? JSON.parse(grouping.products_json)
        : [];
    const mustInclude: {id: string}[] = grouping.must_include_json
        ? JSON.parse(grouping.must_include_json)
        : [];
    const mustExclude: {id: string}[] = grouping.must_exclude_json
        ? JSON.parse(grouping.must_exclude_json)
        : [];

    if (products.length > 0 && !products.some((id) => userDbIds.has(id)))
        return false;
    if (
        mustInclude.length > 0 &&
        !mustInclude.every((item) => userDbIds.has(item.id))
    )
        return false;
    if (mustExclude.some((item) => userDbIds.has(item.id))) return false;

    return true;
}

export function usePortalData() {
    const {data: homeData, isLoading: homeLoading} =
        dibAppApi.endpoints.getHome.useQuery();
    const {filterlist} = homeData || {};
    const databasesAndInternals = [
        ...(filterlist?.databases || []),
        ...(filterlist?.internals || []),
    ];

    const {data: user, isLoading: userLoading} =
        bffApi.endpoints.getUser.useQuery();

    const {data: portalGrouping, isLoading: portalGroupingLoading} =
        dibAppApi.endpoints.getPortalGroupings.useQuery();

    const language = user
        ? getApiLanguageCode(getLangCodeFromEnum(user.spraakId as LanguageEnum))
        : undefined;

    const {
        data: portalRaw,
        isLoading: portalLoading,
        isError,
    } = dibAppApi.endpoints.getPortalByLanguage.useQuery(
        {language: language!},
        {skip: !language}
    );

    const isLoading =
        userLoading || portalLoading || homeLoading || portalGroupingLoading;

    if (isLoading || isError || !portalRaw) {
        return {
            relevantWidgets: [],
            relevantGroupings: [],
            isLoading,
            isError,
        };
    }

    const portal = portalRaw as PortalFull;
    const userDbIds = new Set(databasesAndInternals.map((db) => db.id));
    const userDbIdsNumeric = new Set(
        databasesAndInternals.map((db) => Number(db.id))
    );

    const relevantWidgets = portal.widgets
        .filter((w) => {
            if (!w.is_enabled) return false;
            if (!w.conditions || w.conditions.length === 0) return false;
            if (
                !w.title_target_url &&
                (!w.links ||
                    w.links.length === 0 ||
                    w.links.every((link) => !link.targetUrl))
            )
                return false;
            return w.conditions.some((group) =>
                group.every((id) => userDbIdsNumeric.has(id))
            );
        })
        .sort((a, b) => a.display_order - b.display_order);

    const relevantGroupings = (portalGrouping || [])
        .filter((g) => isGroupingRelevant(g, userDbIds))
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

    return {relevantWidgets, relevantGroupings, isLoading, isError};
}
